import React from "react";
import { motion } from "framer-motion";
import { Quote, Star, ExternalLink } from "lucide-react";

const GOOGLE_PROFILE_URL = "https://share.google/680qslTwKiZbp5Vum";

const testimonials = [
  {
    quote:
      "I hired Karnival events on two separate occasions and they were amazing! From the communication, quality and set up, everything was perfect. They were able to execute the inspiration pictures we sent. We will absolutely hire them again for future events and highly recommend to anyone :)",
    name: "L L",
    role: "Local Guide · 22 weeks ago",
  },
  {
    quote:
      "DJ Avtaar (Ragav) played at our event this past weekend and he was phenomenal!! His music rocked the dance floor and he kept all the (100+) guests dancing the night away with his awesome playlist and amazing DJ'ing skills!! He understood our specific music needs and made sure the night flowed seamlessly. Highly recommend DJ Avtaar for your next event/party!!",
    name: "Rajul Mistry",
    role: "Local Guide · Feb 10, 2025",
  },
  {
    quote:
      "Highly recommended!! I couldn't be happier with the decoration! From the initial consultation to the final reveal, everything was handled professionally and with great creativity. The attention to detail, color coordination, and overall aesthetic perfectly matched what I had envisioned (and even exceeded it). Guests couldn't stop complimenting the decor — it truly brought the whole event to life.",
    name: "Manvinder Singh",
    role: "27 weeks ago",
  },
  {
    quote:
      "We had DJ Avtaar for our engagement party and he was absolutely amazing! From the moment the music started, the energy was high and the dance floor was never empty. He knew exactly how to read the crowd. Also, the decor done by Karnival Events was so elegant and beautifully set the mood. Everything came together perfectly and made the night truly memorable.",
    name: "Lahari Tummalapudi",
    role: "40 weeks ago",
  },
  {
    quote:
      "Planning my own wedding was overwhelming at times, but what truly saved me was finding vendors who just got what I needed. I didn't have to micromanage — DJ Avtaar understood my vision, budget, stayed professional, and delivered results even better than I imagined. Having the right DJ made all the difference on my big day.",
    name: "Harpreet Kaur",
    role: "45 weeks ago",
  },
  {
    quote:
      "We had DJ Avtaar for our son's wedding reception. He also provided the DJ truck for the baraat procession. A perfect fit for both events. We had almost all 200 guests dancing at times and everyone was having a blast. He is exceptional at reading the crowd and adapting. Thank you for making both our events so special.",
    name: "V G",
    role: "48 weeks ago",
  },
  {
    quote:
      "Had the DJ over for an event — he really set the stage. It was an amazing experience. Everyone enjoyed.",
    name: "Pankaj Mahajan",
    role: "26 weeks ago",
  },
];

// Duplicate for seamless marquee
const marqueeItems = [...testimonials, ...testimonials];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-[#0a0a0a] border-y border-white/5 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-[#FFFFFF]" />
          <span className="text-xs uppercase tracking-[0.28em] text-[#FFFFFF]">
            What Our Clients Say
          </span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            data-testid="testimonials-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] text-white tracking-tight max-w-3xl"
          >
            Stories from nights that{" "}
            <span className="italic text-[#FFFFFF]">still echo</span>.
          </motion.h2>

          {/* Google rating badge */}
          <motion.a
            href={GOOGLE_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-rating-badge"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="group inline-flex items-center gap-5 border border-white/10 hover:border-[#FFFFFF]/60 rounded-sm px-6 py-4 bg-[#111111] transition-colors duration-300 shrink-0"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-1">
                Rated on Google
              </div>
              <div className="flex items-center gap-2">
                <span className="font-serif text-3xl text-white leading-none">
                  5.0
                </span>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-3.5 w-3.5 fill-[#FFFFFF] text-[#FFFFFF]"
                    />
                  ))}
                </div>
              </div>
              <div className="text-xs text-white/50 mt-1">
                Based on 7 Google reviews
              </div>
            </div>
            <ExternalLink
              className="h-4 w-4 text-white/40 group-hover:text-[#FFFFFF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              strokeWidth={1.5}
            />
          </motion.a>
        </div>
      </div>

      {/* Marquee row */}
      <div className="relative w-full" data-testid="testimonials-marquee">
        <div className="flex w-max karnival-marquee gap-8 md:gap-14 px-6 md:px-12">
          {marqueeItems.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-${i % testimonials.length}`}
              className="w-[82vw] sm:w-[500px] md:w-[560px] shrink-0 bg-[#111111] border border-white/5 rounded-sm p-8 md:p-10 hover:border-[#FFFFFF]/30 transition-colors duration-500 flex flex-col"
            >
              <Quote
                className="h-8 w-8 text-[#FFFFFF] mb-6 shrink-0"
                strokeWidth={1.2}
              />
              <blockquote className="font-serif text-lg md:text-xl leading-[1.5] text-white/90 font-light italic tracking-tight flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {t.name}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5 tracking-wide truncate">
                    {t.role}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-3.5 w-3.5 fill-[#FFFFFF] text-[#FFFFFF]"
                    />
                  ))}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Fade edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10" />
      </div>
    </section>
  );
}
